import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { SystemMetrics } from "@/types";
import { useQuery } from "@tanstack/react-query";
import { Activity, Clock, Cpu, HardDrive, LucideIcon, Network } from "lucide-react";
import { useTheme } from "@/contexts/useTheme";

function MetricCard({
  title,
  value,
  icon: Icon,
  unit,
  max,
}: {
  title: string;
  value: number;
  icon: LucideIcon;
  unit: string;
  max?: number;
}) {
  const percentage = max ? (value / max) * 100 : value;
  const color =
    percentage > 80
      ? "text-red-600 bg-red-100"
      : percentage > 60
      ? "text-yellow-600 bg-yellow-100"
      : "text-green-600 bg-green-100";

  return (
    <div className="rounded-lg bg-white dark:bg-zinc-800 p-6 shadow-sm border border-gray-200 dark:border-zinc-700">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</p>
          <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
            {value.toFixed(1)}
            {unit}
          </p>
        </div>
        <div className={`rounded-full p-3 ${color.split(" ")[1]}`}>
          <Icon className={`h-6 w-6 ${color.split(" ")[0]}`} />
        </div>
      </div>
      {max && (
        <div className="w-full bg-gray-200 dark:bg-zinc-600 rounded-full h-2">
          <div
            className={`h-2 rounded-full ${
              percentage > 80
                ? "bg-red-600"
                : percentage > 60
                ? "bg-yellow-600"
                : "bg-green-600"
            }`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      )}
    </div>
  );
}

export function SystemPage() {
  const { resolvedTheme, setTheme } = useTheme();
  const { data: metrics, isLoading } = useQuery<SystemMetrics>({
    queryKey: ["system-metrics"],
    queryFn: api.getSystemMetrics,
    refetchInterval: 5000, // Refetch every 5 seconds
  });

  const isDarkMode = resolvedTheme === "dark";

  const toggleDarkMode = () => {
    setTheme(isDarkMode ? "light" : "dark");
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 dark:border-zinc-600 border-t-brand-green mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading system metrics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-0">
      <PageHeader title="System Health" subtitle="Monitor system performance and configuration" />

      {/* System Metrics */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 mb-8">
        <MetricCard
          title="CPU Usage"
          value={metrics?.cpuUsage || 0}
          icon={Cpu}
          unit="%"
          max={100}
        />
        <MetricCard
          title="Memory Usage"
          value={metrics?.memoryUsage || 0}
          icon={Activity}
          unit="%"
          max={100}
        />
        <MetricCard
          title="Disk Usage"
          value={metrics?.diskUsage || 0}
          icon={HardDrive}
          unit="%"
          max={100}
        />
        <MetricCard
          title="Active Connections"
          value={metrics?.activeConnections || 0}
          icon={Network}
          unit=""
        />
      </div>

      {/* Performance Metrics */}
      <div className="grid gap-6 md:grid-cols-3 mb-8">
        <div className="rounded-lg bg-white dark:bg-zinc-800 p-6 shadow-sm border border-gray-200 dark:border-zinc-700">
          <div className="flex items-center gap-3 mb-4">
            <Clock className="h-6 w-6 text-brand-green" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Uptime</h3>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">
            {Math.floor((metrics?.uptime || 0) / 86400)}d{" "}
            {Math.floor(((metrics?.uptime || 0) % 86400) / 3600)}h
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">System uptime</p>
        </div>

        <div className="rounded-lg bg-white dark:bg-zinc-800 p-6 shadow-sm border border-gray-200 dark:border-zinc-700">
          <div className="flex items-center gap-3 mb-4">
            <Activity className="h-6 w-6 text-green-600" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Requests/min</h3>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">
            {metrics?.requestsPerMinute || 0}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Current request rate</p>
        </div>

        <div className="rounded-lg bg-white dark:bg-zinc-800 p-6 shadow-sm border border-gray-200 dark:border-zinc-700">
          <div className="flex items-center gap-3 mb-4">
            <Activity className="h-6 w-6 text-ai-accent" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Avg Response Time</h3>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">
            {(metrics?.averageResponseTime || 0).toFixed(0)}ms
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Average API response</p>
        </div>
      </div>

      {/* Error Rate */}
      <div className="rounded-lg bg-white dark:bg-zinc-800 p-6 shadow-sm border border-gray-200 dark:border-zinc-700 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Error Rate</h2>
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <p className="text-4xl font-bold text-gray-900 dark:text-white">
              {(metrics?.errorRate || 0).toFixed(2)}%
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Current error rate</p>
          </div>
          <div className="flex-1">
            <div className="h-24 bg-gray-50 dark:bg-zinc-700 rounded-lg flex items-center justify-center">
              <p className="text-gray-500 dark:text-gray-400">Error rate chart</p>
            </div>
          </div>
        </div>
      </div>

      {/* System Configuration */}
      <div className="rounded-lg bg-white dark:bg-zinc-800 p-6 shadow-sm border border-gray-200 dark:border-zinc-700">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">System Configuration</h2>
          <button className="rounded-md bg-brand-green px-4 py-2 text-sm font-semibold text-white hover:bg-brand-green/90 transition-colors">
            Edit Configuration
          </button>
        </div>
        <div className="space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-zinc-700">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">Dark Mode</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Enable dark theme for the admin dashboard</p>
            </div>
            <button
              onClick={toggleDarkMode}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-green focus:ring-offset-2 ${
                isDarkMode ? "bg-brand-green" : "bg-gray-200"
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  isDarkMode ? "translate-x-5" : "translate-x-0"
                }`}
              ></span>
            </button>
          </div>
          <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-zinc-700">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">Maintenance Mode</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Enable to restrict access</p>
            </div>
            <button className="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-green focus:ring-offset-2">
              <span className="translate-x-0 inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"></span>
            </button>
          </div>
          <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-zinc-700">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">Auto Backup</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Daily automated backups</p>
            </div>
            <button className="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-brand-green transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-green focus:ring-offset-2">
              <span className="translate-x-5 inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"></span>
            </button>
          </div>
          <div className="flex items-center justify-between py-3">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">Email Notifications</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">System alerts and updates</p>
            </div>
            <button className="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-brand-green transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-brand-green focus:ring-offset-2">
              <span className="translate-x-5 inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"></span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
